import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Loader2, AlertCircle } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { updateUserProfile } from '../services/authService';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
// import { updateUserProfile } from '../../services/authService'; // Updated import
// import apiClient from '../lib/apiClient'; // Ensure apiClient is removed if not used elsewhere


export default function CompleteProfilePage() {
  const { t } = useTranslation();
  const { user, isLoading: authLoading } = useAuth(); // We need user to check name, but primarily for refetch
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [fullName, setFullName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect if user already has a name or if auth is still loading
  useEffect(() => {
    if (!authLoading && user?.name) {
      navigate('/dashboard'); // Or to the default authenticated route
    }
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!fullName.trim()) {
      setError(t('fullNameRequired'));
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      // Call the new updateUserProfile function
      console.log('Submitting profile update with name:', fullName);
      const response = await updateUserProfile({ name: fullName.trim() });
      console.log('Profile update response:', response);

      if (response) { // updateUserProfile returns the updated user object
        // Invalidate user session to refetch with the new name
        await queryClient.invalidateQueries({ queryKey: ['userSession'] });
        // Wait for refetch or directly update context if possible and desired
        // For simplicity, we rely on the redirection in AuthProvider/useUserSession after refetch
        toast.success(t('profileUpdatedSuccessfully')); // Or use a more sophisticated notification
        navigate('/dashboard'); // Redirect to dashboard or intended page
      } else {
        throw new Error(t('failedToUpdateProfile'));
      }
    } catch (err: any) {
      console.error('Error updating profile:', err);
      // Assuming the error structure from updateUserProfile (via apiClient) will be similar
      setError(err.response?.data?.message || err.message || 'An unknown error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  // If user is somehow null here after authLoading is false, it's an issue (handled by AuthContext redirect usually)
  // but this page shouldn't be reachable if user is null.

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-100 to-blue-100 p-4">
      <div className="w-full max-w-md bg-white shadow-xl rounded-lg p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6 text-center">{t('completeYourProfile')}</h1>
        <p className="text-center text-gray-600 mb-6">
          {t('enterFullNameToContinue')}
        </p>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="fullName" className="text-sm font-medium text-gray-700">{t('fullName')}</Label>
            <Input
              id="fullName"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder={t('fullNamePlaceholder')}
            />
          </div>
          {error && (
            <div className="flex items-center text-sm text-red-600 bg-red-50 p-3 rounded-md">
              <AlertCircle className="h-5 w-5 mr-2" />
              <span>{error}</span>
            </div>
          )}
          <div>
            <Button type="submit" disabled={isLoading || !fullName.trim()} className="w-full flex justify-center py-2 px-4">
              {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : t('saveAndContinue')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
